import { ArrowRightIcon, GitBranchIcon, WarningCircleIcon } from '@phosphor-icons/react'
import _ from 'lodash'
import { Link } from 'react-router'
import { match } from 'ts-pattern'
import { Routes } from '../../../../routes.ts'
import { useGetFreeformUploadSession } from '../../../../utils/api-client/api-hooks.ts'
import type {
  FreeformUploadSessionResponse,
  FreeformUploadSessionScan,
} from '../../../../utils/api-client/api-schemas.ts'
import { formatDuration } from '../../../../utils/date-utils.ts'
import { pluralize } from '../../../../utils/text-utils.ts'
import { TooltipWithHover } from '../../../tooltip.tsx'
import { groupUnassociatedByReason, LOW_CONFIDENCE_THRESHOLD } from '../../freeform-unassociated-reasons.ts'
import { MissGroups, StatCell } from '../freeform-run-parts.tsx'
import * as sharedStyles from '../freeform-run-styles.css.ts'
import {
  failureReasonOf,
  findingLabelOf,
  type FreeformRunState,
  hasProgress,
  type RunRepoScan,
  verifiedLocationsOf,
  whereOf,
} from '../freeform-run.ts'
import * as styles from './freeform-finished-view.css.ts'

export type FreeformFinishedViewProps = {
  sessionId: string
  /** The run folded from the session's stream; empty when the platform no longer holds its history. */
  run: FreeformRunState
}

export const FreeformFinishedView: React.FC<FreeformFinishedViewProps> = ({ sessionId, run }) => {
  const { data: session } = useGetFreeformUploadSession({ sessionId })
  return session ? <FreeformFinishedViewPure session={session} run={run} /> : null
}

export type FreeformFinishedViewPureProps = {
  session: FreeformUploadSessionResponse
  run: FreeformRunState
}

/**
 * Where a finished Findings Intake run ended: the scans it created, with the line each finding landed on,
 * and the findings that need a human look. Built from the session the platform stored; the run's stream,
 * when it is still available, adds each finding's title and location.
 */
export const FreeformFinishedViewPure: React.FC<FreeformFinishedViewPureProps> = ({ session, run }) => {
  const placed = _.sumBy(session.scans, scan => scan.finding_count)
  const unplaced = session.unassociated.length
  const total = run.total ?? placed + unplaced
  const isFailed = session.status === 'failed'
  const unreadFiles = session.files.filter(file => file.parse_status === 'unextracted')

  return (
    <div className={sharedStyles.view}>
      <section className={styles.outcome} aria-label="Run outcome">
        <div>
          <p className={styles.outcomeMeta}>
            {session.files.map(file => file.filename).join(', ')} · finished in{' '}
            {formatDuration(session.created_at, session.updated_at)}
          </p>
          <h2 className={styles.outcomeTitle}>
            {isFailed
              ? 'This run stopped before it finished'
              : `${total} ${pluralize(total, 'finding', 'findings')} became ${session.scans.length} ${pluralize(session.scans.length, 'scan', 'scans')}`}
          </h2>
          <p className={styles.outcomeText}>{outcomeSentence({ placed, unplaced, isFailed })}</p>
        </div>
        <OutcomeStats session={session} run={run} total={total} />
      </section>

      {isFailed && (
        <section className={styles.failed} role="alert" aria-label="Run failed">
          <p className={styles.failedTitle}>Pixee could not finish this upload</p>
          <p className={styles.failedText}>{failureReasonOf(session)}</p>
        </section>
      )}

      {session.scans.length > 0 && (
        <div className={styles.scans}>
          {session.scans.map(scan => (
            <ScanCard
              key={scan.scan_id}
              scanId={scan.scan_id}
              repoFullName={scan.repo_full_name}
              findingCount={scan.finding_count}
              rows={rowsOf(scan, run)}
              streamed={run.repoScans.find(streamedScan => streamedScan.scanId === scan.scan_id)}
            />
          ))}
        </div>
      )}

      {unplaced > 0 && <NeedsAHumanLook session={session} />}

      {unreadFiles.length > 0 && (
        <p className={styles.files}>
          Pixee could not read {unreadFiles.map(file => file.filename).join(', ')}. Upload{' '}
          {pluralize(unreadFiles.length, 'it', 'them')} in another format to include{' '}
          {pluralize(unreadFiles.length, 'its', 'their')} findings.
        </p>
      )}
    </div>
  )
}

const outcomeSentence = ({ placed, unplaced, isFailed }: { placed: number; unplaced: number; isFailed: boolean }) =>
  match({ placed, unplaced, isFailed })
    .with({ isFailed: true, placed: 0 }, () => 'No scans were created. Upload the files again to retry.')
    .with(
      { isFailed: true },
      () => `${placed} ${pluralize(placed, 'finding was', 'findings were')} placed in code before it stopped.`
    )
    .with({ placed: 0, unplaced: 0 }, () => 'The report held no findings Pixee could act on.')
    .with({ unplaced: 0 }, () => `${placed} ${pluralize(placed, 'is', 'are')} placed in code and already in triage.`)
    .with(
      { placed: 0 },
      () => `None could be tied to code Pixee can scan; ${pluralize(unplaced, 'it needs', 'they need')} a human look.`
    )
    .otherwise(
      () =>
        `${placed} ${pluralize(placed, 'is', 'are')} placed in code and already in triage. ${unplaced} ${pluralize(unplaced, 'needs', 'need')} a human look.`
    )

const OutcomeStats: React.FC<{ session: FreeformUploadSessionResponse; run: FreeformRunState; total: number }> = ({
  session,
  run,
  total,
}) => {
  // A run whose stream history is gone still has the stored counts; the stream adds how it got there.
  const isStreamed = hasProgress(run)
  const cells = (
    [
      [total, 'findings read'],
      [session.scans.length, pluralize(session.scans.length, 'scan created', 'scans created')],
      isStreamed ? [verifiedLocationsOf(run).length, 'locations verified'] : undefined,
      isStreamed ? [run.searches, 'code searches'] : undefined,
      isStreamed ? [run.copiesIgnored, 'copies ignored'] : undefined,
    ] satisfies ([number, string] | undefined)[]
  ).filter(cell => cell !== undefined)
  return (
    <div className={styles.outcomeStats}>
      {cells.map(([value, label]) => (
        <StatCell key={label} value={value} label={label} className={styles.outcomeStat} />
      ))}
    </div>
  )
}

const severityLevel = (severity: string) =>
  match(severity.toLowerCase())
    .with('critical', 'high', () => 'high' as const)
    .with('medium', () => 'medium' as const)
    .otherwise(() => 'low' as const)

/** One finding on a scan card. */
type ScanRow = {
  key: string
  /** The report's own id for it, which only the run's stream knows. */
  sourceId?: string
  title: string
  where: string
  path: string
  severity?: string
  /** The platform's id for it, to open it on its scan. */
  findingId?: string
}

/**
 * A scan's findings: from the run's stream while the platform still holds it, which knows each finding's
 * report id and line, otherwise as the platform stored them on the scan, at their file.
 */
const rowsOf = (scan: FreeformUploadSessionScan, run: FreeformRunState): ScanRow[] => {
  const streamed = run.findings.flatMap(finding => {
    const location = finding.locations.find(candidate => candidate.state === 'verified')
    return finding.repository === scan.repo_full_name && location
      ? [
          {
            key: `run-${finding.number}`,
            sourceId: findingLabelOf(finding),
            title: finding.title,
            where: whereOf(location),
            path: location.path,
            severity: finding.severity,
          },
        ]
      : []
  })
  if (streamed.length > 0) {
    return streamed
  }
  return scan.findings.map(finding => ({
    key: finding.finding_id,
    title: finding.title ?? 'Untitled finding',
    where: whereOf({ path: finding.path }),
    path: finding.path,
    severity: finding.severity,
    findingId: finding.finding_id,
  }))
}

type ScanCardProps = {
  scanId: string
  repoFullName: string
  findingCount: number
  rows: ScanRow[]
  streamed?: RunRepoScan
}

const ScanCard: React.FC<ScanCardProps> = ({ scanId, repoFullName, findingCount, rows, streamed }) => {
  const isLowConfidence =
    streamed?.lowestConfidence !== undefined && streamed.lowestConfidence < LOW_CONFIDENCE_THRESHOLD
  return (
    <section className={styles.scan} aria-label={`Scan of ${repoFullName}`}>
      <div className={styles.scanBar} />
      <div className={styles.scanHead}>
        <GitBranchIcon size={16} aria-hidden />
        <span className={styles.scanName} title={repoFullName}>
          {repoFullName}
        </span>
        <span className={styles.scanCount}>{findingCount}</span>
      </div>
      {rows.length > 0 ? (
        <ul className={styles.scanFindings}>
          {rows.map(row => (
            <li key={row.key} className={styles.scanFinding}>
              <span className={sharedStyles.findingId}>{row.sourceId}</span>
              <span className={styles.findingText}>
                {row.findingId ? (
                  <Link
                    className={styles.findingLink}
                    title={row.title}
                    to={Routes.ScanDetailsPage.createPath({ scanId, triageFindingId: row.findingId })}
                  >
                    {row.title}
                  </Link>
                ) : (
                  <span className={styles.findingTitle} title={row.title}>
                    {row.title}
                  </span>
                )}
                <span className={styles.findingWhere} title={row.path}>
                  {row.where}
                </span>
              </span>
              {row.severity && (
                <span className={styles.severity({ level: severityLevel(row.severity) })}>{row.severity}</span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.scanNote}>
          {findingCount} {pluralize(findingCount, 'finding', 'findings')} placed in this repository.
        </p>
      )}
      <div className={styles.scanFoot}>
        <Link className={styles.scanLink} to={Routes.ScanDetailsPage.createPath({ scanId })}>
          View scan
          <ArrowRightIcon size={14} weight="bold" aria-hidden />
        </Link>
        {isLowConfidence && (
          <TooltipWithHover
            trigger={
              <span className={styles.lowConfidence} tabIndex={0}>
                <WarningCircleIcon size={12} weight="fill" aria-hidden />
                Low confidence
              </span>
            }
          >
            {streamed.weakestRationale ?? 'Pixee was not confident every finding belongs to this repository.'}
          </TooltipWithHover>
        )}
      </div>
    </section>
  )
}

const NeedsAHumanLook: React.FC<{ session: FreeformUploadSessionResponse }> = ({ session }) => (
  <section className={styles.misses} aria-label="Needs a human look">
    <div>
      <h3 className={styles.missesTitle}>Needs a human look</h3>
      <p className={styles.missesText}>Not judged safe. Pixee could not tie these to code it can scan.</p>
    </div>
    <MissGroups
      className={styles.missGroups}
      groupClassName={styles.missGroup}
      isDescribed
      groups={groupUnassociatedByReason(session.unassociated).map(group => ({
        ...group,
        findings: group.findings.map((finding, index) => ({
          key: `${group.reason}-${index}`,
          title: finding.title,
          rationale: finding.rationale,
        })),
      }))}
    />
  </section>
)
